<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="Kullanıcılar"
      title="Premium"
      description="Premium kullanıcıları, erişim statelerini ve paket katalogunu tek yönetim yüzeyinden yönetin."
    >
      <template #actions>
        <v-btn
          color="primary"
          small
          outlined
          @click="openAssignDialog()"
        >
          <v-icon
            left
            small
          >
            {{ icons.mdiAccountPlusOutline }}
          </v-icon>
          Kullanıcı Ata
        </v-btn>
        <v-btn
          outlined
          color="primary"
          small
          :loading="loading"
          @click="fetchOverview()"
        >
          Yenile
        </v-btn>
        <v-menu offset-y>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              aria-label="Gelişmiş Premium işlemleri"
              title="Gelişmiş işlemler"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>{{ icons.mdiDotsVertical }}</v-icon>
            </v-btn>
          </template><v-list dense>
            <v-list-item
              :disabled="submitting"
              @click="openCleanupDialog()"
            >
              <v-list-item-title>Bakım önizlemesi</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </admin-page-header>

    <v-alert
      v-if="errorMessage"
      dense
      outlined
      type="error"
      class="mb-4"
    >
      {{ errorMessage }}
    </v-alert>

    <details class="text-caption text--secondary mb-2">
      <summary>Sayım kapsamı</summary><div>
        Kullanıcı özeti: benzersiz kullanıcılar sayılır, tablo filtrelerinden etkilenmez.
        Toplam = aktif erişim + on hold + bitmiş. Aktif manuel, aktif erişimin alt kümesidir.
      </div>
    </details>
    <admin-metric-strip
      :items="summaryCards"
      :loading="loading"
    />

    <div class="d-flex flex-wrap mt-4 mb-2">
      <v-chip
        v-for="item in recordSummary"
        :key="item.label"
        small
        outlined
        class="mr-2 mb-2"
      >
        {{ item.label }}: {{ item.value }} kayıt
      </v-chip>
    </div>

    <div class="text-caption text--secondary mb-2">
      Sahibi bilinmeyen kayıtlar kullanıcı sayılarına dahil değildir.
      Aşağıdaki tabloda her satır bir üyelik kaydıdır; aynı kullanıcı birden fazla satırda yer alabilir.
    </div>

    <v-row>
      <v-col
        cols="12"
      >
        <v-card
          class="admin-table-card"
          outlined
        >
          <div class="admin-table-toolbar premium-toolbar">
            <div class="premium-toolbar__meta">
              <div class="admin-table-toolbar__title">
                Premium üyelik kayıtları
              </div>
              <v-chip
                small
                outlined
                color="primary"
              >
                {{ users.length }} kayıt
              </v-chip>
            </div>

            <div class="premium-toolbar__controls">
              <v-select
                v-model="premiumStatus"
                :items="statusOptions"
                item-text="text"
                item-value="value"
                label="Premium durumu"
                hide-details
                outlined
                dense
                class="premium-status-filter"
                @change="fetchOverview()"
              ></v-select>
              <v-text-field
                v-model="searchValue"
                append-icon="mdi-magnify"
                label="Premium kaydı ara"
                placeholder="Premium ID, kullanıcı ID, ad veya e-posta"
                single-line
                hide-details
                outlined
                dense
                clearable
                class="premium-search"
                @input="onSearchInput"
              ></v-text-field>
            </div>
          </div>

          <v-data-table
            :headers="userHeaders"
            :page.sync="listPage"
            :items-per-page.sync="listSize"
            :items="users"
            item-key="premiumUserId"
            :loading="loading"
            loading-text="Premium verileri yükleniyor"
            :footer-props="tableFooterProps"
            class="admin-data-table elevation-0"
          >
            <template v-slot:item.premiumUserId="{ item }">
              <span class="font-weight-bold">#{{ item.premiumUserId }}</span>
            </template>

            <template v-slot:item.user="{ item }">
              <div class="premium-user-cell">
                <template v-if="premiumOwnerResolved(item)">
                  <div class="font-weight-bold">
                    {{ item.fullName || '-' }}
                  </div>
                  <div class="text-caption text--secondary">
                    #{{ item.id }} · {{ item.email }}
                  </div>
                </template>
                <template v-else>
                  <div class="font-weight-medium text--secondary">
                    Sahibi bilinmeyen tarihsel kayıt
                  </div>
                  <div class="text-caption text--secondary">
                    Eski sistemde kullanıcı ilişkisi korunmamış
                  </div>
                </template>
              </div>
            </template>

            <template v-slot:item.packageTime="{ item }">
              <div>
                <div class="font-weight-medium">
                  {{ premiumListPlanLabel(item) }}
                </div>
                <div class="text-caption text--secondary">
                  {{ packageMeta(item) }}
                </div>
              </div>
            </template>

            <template v-slot:item.premiumAccessState="{ item }">
              <v-chip
                x-small
                label
                :color="stateColor(item.premiumAccessState)"
                :text-color="stateTextColor(item.premiumAccessState)"
              >
                {{ stateLabel(item.premiumAccessState) }}
              </v-chip>
            </template>

            <template v-slot:item.premiumPlatform="{ item }">
              <span class="text-caption font-weight-medium">
                {{ platformLabel(resolvedPlatform(item)) }}
              </span>
            </template>

            <template v-slot:item.premiumPurchaseDate="{ item }">
              <span class="text-caption">
                {{ formatTimestamp(item.premiumPurchaseDate) }}
              </span>
            </template>

            <template v-slot:item.premiumAccessEndDate="{ item }">
              <span class="text-caption">
                {{ formatTimestamp(item.premiumAccessEndDate) }}
              </span>
            </template>

            <template v-slot:item.customPremium="{ item }">
              <v-chip
                x-small
                label
                :color="item.customPremium ? 'warning' : 'secondary'"
              >
                {{ item.customPremium ? 'Manuel' : 'Store' }}
              </v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="premium-actions">
                <v-btn
                  icon
                  small
                  color="primary"
                  :to="premiumDetailRoute(item)"
                  :aria-label="`Premium #${item.premiumUserId} detayını aç`"
                  title="Premium detayını aç"
                >
                  <v-icon>{{ icons.mdiEyeOutline }}</v-icon>
                </v-btn>
                <v-btn
                  v-if="item.attachedToUser"
                  icon
                  small
                  color="primary"
                  @click="openAssignDialog(item)"
                >
                  <v-icon>{{ icons.mdiPencilOutline }}</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <v-col
        cols="12"
      >
        <v-card
          class="admin-table-card"
          outlined
        >
          <v-toolbar
            dense
            :elevation="0"
            class="admin-page-toolbar"
          >
            <v-toolbar-title>Paketler</v-toolbar-title>
          </v-toolbar>

          <v-data-table
            :headers="packageHeaders"
            :items="packages"
            :footer-props="tableFooterProps"
            class="admin-data-table elevation-0"
          >
            <template v-slot:item.packagePrice="{ item }">
              <span class="font-weight-medium">
                {{ formatCurrency(item.packagePrice) }}
              </span>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="premium-actions">
                <v-btn
                  icon
                  small
                  color="primary"
                  @click="openPackageDialog(item)"
                >
                  <v-icon>{{ icons.mdiPencilOutline }}</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog
      v-model="assignDialog"
      max-width="420px"
    >
      <v-card class="admin-dialog-card">
        <v-toolbar
          dense
          :elevation="0"
        >
          <v-toolbar-title>Premium Ataması</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="assignDialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-6">
          <div
            v-if="selectedUser"
            class="text-caption text--secondary mb-4"
          >
            {{ selectedUser.fullName }} · {{ selectedUser.email }}
          </div>
          <v-autocomplete
            v-model="selectedUser"
            :items="assignUserOptions"
            :search-input.sync="assignUserSearch"
            item-text="display"
            return-object
            hide-no-data
            outlined
            dense
            clearable
            label="Kullanıcı"
            placeholder="Ad, soyad veya e-posta ile ara"
            :loading="assignUserLoading"
            class="mb-4"
            @update:search-input="onAssignUserSearch"
          ></v-autocomplete>
          <v-select
            v-model="assignForm.packageId"
            :items="packageSelectItems"
            item-text="text"
            item-value="value"
            label="Premium paketi"
            outlined
            dense
          ></v-select>
        </v-card-text>
        <v-card-actions class="px-6 pb-6">
          <v-btn
            v-if="canClearManualAssignment"
            outlined
            color="error"
            :loading="submitting"
            @click="clearAssignment(selectedUser)"
          >
            Premium Sil
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="primary"
            @click="assignDialog = false"
          >
            İptal
          </v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            @click="assignPackage()"
          >
            Kaydet
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="packageDialog"
      max-width="420px"
    >
      <v-card class="admin-dialog-card">
        <v-toolbar
          dense
          :elevation="0"
        >
          <v-toolbar-title>Paket Fiyatını Düzenle</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="closePackageDialog()"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="packageForm.packageTime"
                outlined
                dense
                readonly
                label="Paket adı"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model.number="packageForm.day"
                outlined
                dense
                type="number"
                readonly
                label="Gün"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model.number="packageForm.packagePrice"
                outlined
                dense
                type="number"
                label="Ücret"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-6 pb-6">
          <v-spacer></v-spacer>
          <v-btn
            text
            color="primary"
            @click="closePackageDialog()"
          >
            İptal
          </v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            @click="savePackage()"
          >
            Kaydet
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="cleanupDialog"
      max-width="760px"
      :persistent="submitting"
    >
      <v-card class="admin-dialog-card">
        <v-toolbar
          dense
          :elevation="0"
        >
          <v-toolbar-title>Premium bakım önizlemesi</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            :disabled="submitting"
            aria-label="Bakım önizlemesini kapat"
            @click="cleanupDialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-6">
          <v-alert
            dense
            text
            type="info"
            class="mb-4"
          >
            Önizleme hiçbir kaydı değiştirmez. Bakımı uygularsanız uygun Premium kayıtları sunucudaki bakım kurallarına göre güncellenir.
          </v-alert>
          <v-alert
            v-if="cleanupError"
            type="error"
            dense
            outlined
          >
            {{ cleanupError }}
          </v-alert>
          <div class="premium-cleanup-grid">
            <div class="premium-cleanup-metric">
              <span class="text-caption text--secondary">Otomatik aksiyon</span>
              <strong>{{ cleanupPreview.automaticActionCount || 0 }}</strong>
            </div>
            <div class="premium-cleanup-metric">
              <span class="text-caption text--secondary">Manuel inceleme</span>
              <strong>{{ cleanupPreview.manualReviewCount || 0 }}</strong>
            </div>
          </div>
          <div class="premium-cleanup-section">
            <div class="font-weight-bold mb-2">
              Erişim durumu dağılımı
            </div>
            <v-chip
              v-for="(count, key) in cleanupLifecycleStates"
              :key="key"
              small
              outlined
              class="mr-2 mb-2"
            >
              {{ stateLabel(key) }} · {{ count }}
            </v-chip>
          </div>
          <div class="premium-cleanup-section">
            <div class="font-weight-bold mb-2">
              Otomatik aksiyonlar
            </div>
            <v-simple-table dense>
              <tbody>
                <tr
                  v-for="item in cleanupPreview.automaticActions || []"
                  :key="`${item.action}-${item.userId}`"
                >
                  <td class="text-caption font-weight-medium">
                    {{ item.action }}
                  </td>
                  <td class="text-caption">
                    {{ item.email }}
                  </td>
                  <td class="text-caption">
                    {{ item.accessState || '-' }}
                  </td>
                </tr>
                <tr v-if="!(cleanupPreview.automaticActions || []).length">
                  <td
                    colspan="3"
                    class="text-caption text--secondary"
                  >
                    Önerilen aksiyon yok.
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
          <v-checkbox
            v-model="cleanupConfirmed"
            :disabled="submitting"
            label="Gösterilen bakım etkilerini inceledim ve uygulanmasını onaylıyorum."
          />
        </v-card-text>
        <v-card-actions class="px-6 pb-6">
          <v-spacer></v-spacer>
          <v-btn
            text
            color="primary"
            :disabled="submitting"
            @click="cleanupDialog = false"
          >
            Kapat
          </v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            :disabled="!cleanupConfirmed || !cleanupPreview.automaticActionCount"
            @click="runCleanup()"
          >
            Bakımı Uygula
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar"
      timeout="3200"
      color="success"
    >
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios'
import {
  mdiAccountPlusOutline,
  mdiDotsVertical,
  mdiClose,
  mdiCrownOutline,
  mdiEyeOutline,
  mdiPencilOutline,
  mdiShieldCheckOutline,
  mdiStarFourPointsOutline,
  mdiTimerSand,
} from '@mdi/js'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import AdminMetricStrip from '@/components/admin/AdminMetricStrip.vue'

export default {
  name: 'PremiumManagement',
  components: {
    AdminPageHeader,
    AdminMetricStrip,
  },
  data() {
    return {
      loading: false,
      submitting: false,
      snackbar: false,
      snackbarMessage: '',
      errorMessage: '',
      searchValue: this.$route.query.search || '',
      premiumStatus: this.$route.query.status || 'ALL',
      listPage: 1,
      listSize: Number(this.$route.query.size) || 10,
      searchTimeout: null,
      assignLookupTimeout: null,
      assignUserSearch: '',
      assignUserLoading: false,
      assignUserOptions: [],
      users: [],
      packages: [],
      summary: {
        totalPremiumRecords: 0,
        totalPremiumUsers: 0,
        expiredPremiumUsers: 0,
        attachedPremiumUsers: 0,
        historicalPremiumRecords: 0,
        resolvedHistoricalOwnerRecords: 0,
        unresolvedOwnerRecords: 0,
        accessActiveUsers: 0,
        recoveryOpenUsers: 0,
        customPremiumUsers: 0,
        accessStateCounts: {},
      },
      assignDialog: false,
      packageDialog: false,
      cleanupDialog: false,
      cleanupConfirmed: false,
      cleanupError: '',
      selectedUser: null,
      assignForm: {
        packageId: null,
      },
      packageForm: {
        id: null,
        packageTime: '',
        day: null,
        packagePrice: null,
      },
      cleanupPreview: {},
      icons: {
        mdiAccountPlusOutline,
        mdiDotsVertical,
        mdiClose,
        mdiEyeOutline,
        mdiPencilOutline,
      },
      statusOptions: [
        { text: 'Tümü', value: 'ALL' },
        { text: 'Aktif', value: 'ACTIVE' },
        { text: 'Ödeme tekrar deneniyor', value: 'PAYMENT_RETRY_WITH_ACCESS' },
        { text: 'Erişim beklemede', value: 'ON_HOLD_NO_ACCESS' },
        { text: 'Bitmiş', value: 'EXPIRED' },
        { text: 'Bilinmiyor', value: 'UNKNOWN' },
      ],
      userHeaders: [
        { text: 'Premium ID', value: 'premiumUserId', sortable: false },
        { text: 'Kullanıcı', value: 'user', sortable: false },
        { text: 'Paket', value: 'packageTime', sortable: false },
        { text: 'Durum', value: 'premiumAccessState', sortable: false },
        { text: 'Satın Alma', value: 'premiumPurchaseDate', sortable: false },
        { text: 'Erişim Sonu', value: 'premiumAccessEndDate', sortable: false },
        { text: 'Platform', value: 'premiumPlatform', sortable: false },
        { text: 'Tip', value: 'customPremium', sortable: false },
        {
          text: 'İşlemler', value: 'actions', sortable: false, align: 'center',
        },
      ],
      packageHeaders: [
        { text: 'Paket', value: 'packageTime', sortable: false },
        { text: 'Gün', value: 'day', sortable: false },
        { text: 'Ücret', value: 'packagePrice', sortable: false },
        { text: 'Atama', value: 'assignedUserCount', sortable: false },
        {
          text: 'İşlemler', value: 'actions', sortable: false, align: 'center',
        },
      ],
    }
  },
  computed: {
    summaryCards() {
      return [
        {
          label: 'Toplam premium kullanıcı',
          value: this.summary.totalPremiumUsers || 0,
          helper: 'Premium geçmişi bulunan benzersiz kullanıcılar',
          icon: mdiCrownOutline,
        },
        {
          label: 'Hesaba bağlı premium',
          value: this.summary.attachedPremiumUsers || 0,
          helper: 'Aktif erişimi veya on hold üyeliği olan benzersiz kullanıcılar',
          icon: mdiShieldCheckOutline,
        },
        {
          label: 'Aktif manuel premium',
          value: this.summary.customPremiumUsers || 0,
          helper: 'Manuel atanmış ve şu an erişimi açık benzersiz kullanıcılar',
          icon: mdiCrownOutline,
        },
        {
          label: 'Aktif erişim',
          value: this.summary.accessActiveUsers || 0,
          helper: 'Şu an erişimi açık premium kullanıcı',
          icon: mdiShieldCheckOutline,
        },
        {
          label: 'On hold',
          value: this.summary.recoveryOpenUsers || 0,
          helper: 'Erişimi kapalı, recovery penceresi açık benzersiz kullanıcılar',
          icon: mdiTimerSand,
        },
        {
          label: 'Bitmiş',
          value: this.summary.expiredPremiumUsers || 0,
          helper: 'Aktif erişimi veya on hold üyeliği kalmayan benzersiz kullanıcılar',
          icon: mdiStarFourPointsOutline,
        },
      ]
    },
    recordSummary() {
      return [
        { label: 'Toplam üyelik', value: this.summary.totalPremiumRecords || 0 },
        { label: 'Sahibi eşleşen geçmiş üyelik', value: this.summary.resolvedHistoricalOwnerRecords || 0 },
        { label: 'Sahibi bilinmeyen', value: this.summary.unresolvedOwnerRecords || 0 },
      ]
    },
    packageSelectItems() {
      return this.packages.map(item => ({
        text: `${item.packageTime} · ${item.day} gün · ${this.formatCurrency(item.packagePrice)}`,
        value: item.id,
      }))
    },
    cleanupLifecycleStates() {
      return this.cleanupPreview?.lifecycleSummary?.accessStates || {}
    },
    tableFooterProps() {
      return {
        'items-per-page-options': [10, 20, 50, 100],
      }
    },
    canClearManualAssignment() {
      return !!(
        this.selectedUser
        && this.selectedUser.customPremium
        && this.selectedUser.premiumUserId
      )
    },
  },
  watch: {
    premiumStatus() { this.syncListQuery() },
    listPage() { this.syncListQuery() },
    listSize() { this.syncListQuery() },
  },
  created() {
    const initialPage = Math.max(1, Number(this.$route.query.page) || 1)
    this.fetchOverview().then(() => {
      this.listPage = initialPage
    })
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout)
    clearTimeout(this.assignLookupTimeout)
  },
  methods: {
    syncListQuery() {
      if (this.loading || this.$route.name !== 'premium') return
      const query = {
        search: this.searchValue || undefined,
        status: this.premiumStatus === 'ALL' ? undefined : this.premiumStatus,
        page: String(this.listPage),
        size: String(this.listSize),
      }
      const current = this.$route.query
      if (Object.keys(query).every(key => query[key] === current[key])) return
      this.$router.replace({ name: 'premium', query })
    },
    async fetchOverview(search = this.searchValue) {
      this.loading = true
      this.errorMessage = ''
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}admin/premium/overview`, {
          params: {
            searchKey: undefined,
            search: search || undefined,
            status: this.premiumStatus === 'ALL' ? undefined : this.premiumStatus,
          },
        })
        this.applyOverview(response?.data?.data)
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Premium verileri yüklenemedi.'
      } finally {
        this.loading = false
        this.syncListQuery()
      }
    },
    applyOverview(payload) {
      this.summary = payload?.summary || this.summary
      this.users = payload?.users || []
      this.packages = payload?.packages || []
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.listPage = 1
        this.syncListQuery()
        this.fetchOverview(this.searchValue)
      }, 350)
    },
    openAssignDialog(item) {
      if (item && !item.attachedToUser) {
        return
      }
      this.selectedUser = item || null
      this.assignForm.packageId = item?.packageId || null
      this.assignUserSearch = item?.email || ''
      this.assignUserOptions = item ? [this.toAssignableUser(item)] : []
      this.assignDialog = true
    },
    premiumDetailRoute(item) {
      return {
        name: 'premium-detail',
        params: { premiumId: item.premiumUserId },
        query: {
          search: this.searchValue || undefined,
          status: this.premiumStatus === 'ALL' ? undefined : this.premiumStatus,
          page: String(this.listPage),
          size: String(this.listSize),
        },
      }
    },
    onAssignUserSearch(value) {
      if (!value || value.length < 2) {
        return
      }

      clearTimeout(this.assignLookupTimeout)
      this.assignLookupTimeout = setTimeout(() => {
        this.fetchAssignableUsers(value)
      }, 250)
    },
    async fetchAssignableUsers(search) {
      this.assignUserLoading = true
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}user`, {
          params: {
            page: 0,
            size: 12,
            searchKey: search,
            sortBy: 'created',
            direction: 'DESC',
          },
        })
        this.assignUserOptions = (response?.data?.content || []).map(this.toAssignableUser)
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Kullanıcı araması yapılamadı.'
      } finally {
        this.assignUserLoading = false
      }
    },
    toAssignableUser(item) {
      const fullName = [item?.fullName || item?.name, item?.surname]
        .filter(Boolean)
        .join(' ')
        .trim()

      return {
        id: item.id,
        fullName: fullName || '-',
        email: item.email || '-',
        packageId: item.packageId || item?.premiumUser?.premiumPackage?.id || null,
        display: `${fullName || '-'} · ${item.email || '-'}`,
      }
    },
    async assignPackage() {
      if (!this.selectedUser || !this.assignForm.packageId) {
        return
      }
      this.submitting = true
      try {
        await axios.post(`${process.env.VUE_APP_BASE_API_URL}admin/premium/users/${this.selectedUser.id}/assign`, {
          packageId: this.assignForm.packageId,
        })
        await this.fetchOverview()
        this.assignDialog = false
        this.snackbarMessage = 'Premium ataması güncellendi.'
        this.snackbar = true
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Premium ataması güncellenemedi.'
      } finally {
        this.submitting = false
      }
    },
    async clearAssignment(item) {
      if (!item?.id) {
        return
      }

      this.submitting = true
      try {
        await axios.delete(`${process.env.VUE_APP_BASE_API_URL}admin/premium/users/${item.id}/assignment`)
        await this.fetchOverview()
        this.assignDialog = false
        this.selectedUser = null
        this.assignForm.packageId = null
        this.snackbarMessage = 'Premium ataması kaldırıldı.'
        this.snackbar = true
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Premium ataması kaldırılamadı.'
      } finally {
        this.submitting = false
      }
    },
    openPackageDialog(item) {
      if (!item) {
        return
      }

      this.packageForm = {
        id: item.id,
        packageTime: item.packageTime,
        day: item.day,
        packagePrice: item.packagePrice,
      }
      this.packageDialog = true
    },
    closePackageDialog() {
      this.packageDialog = false
    },
    async savePackage() {
      if (!this.packageForm.id) {
        this.errorMessage = 'Premium paket bulunamadı.'

        return
      }

      this.submitting = true
      try {
        const url = `${process.env.VUE_APP_BASE_API_URL}admin/premium/packages/${this.packageForm.id}`
        await axios.put(url, {
          packagePrice: this.packageForm.packagePrice,
        })
        await this.fetchOverview()
        this.packageDialog = false
        this.snackbarMessage = 'Premium paket fiyatı güncellendi.'
        this.snackbar = true
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Premium paketi kaydedilemedi.'
      } finally {
        this.submitting = false
      }
    },
    async openCleanupDialog() {
      if (this.submitting) return
      this.cleanupConfirmed = false
      this.cleanupError = ''
      this.submitting = true
      try {
        const response = await axios.post(`${process.env.VUE_APP_BASE_API_URL}admin/premium/cleanup?applyChanges=false`)
        this.cleanupPreview = response?.data?.data || {}
        this.cleanupDialog = true
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Cleanup önizlemesi alınamadı.'
      } finally {
        this.submitting = false
      }
    },
    async runCleanup() {
      if (this.submitting || !this.cleanupConfirmed || !this.cleanupPreview.automaticActionCount) return
      this.submitting = true
      this.cleanupError = ''
      try {
        const response = await axios.post(`${process.env.VUE_APP_BASE_API_URL}admin/premium/cleanup?applyChanges=true`)
        this.cleanupPreview = response?.data?.data || {}
        this.cleanupConfirmed = false
        await this.fetchOverview()
        this.snackbarMessage = 'Premium bakımı uygulandı.'
        this.snackbar = true
      } catch (error) {
        this.cleanupError = 'Premium bakımı uygulanamadı. Lütfen tekrar deneyin.'
      } finally {
        this.submitting = false
      }
    },
    stateLabel(value) {
      if (!value) {
        return 'Bilinmiyor'
      }

      return {
        ACTIVE: 'Aktif',
        PAYMENT_RETRY_WITH_ACCESS: 'Retry + erişim',
        ON_HOLD_NO_ACCESS: 'On hold',
        EXPIRED: 'Bitmiş',
      }[value] || value
    },
    premiumOwnerResolved(item) {
      return item?.ownerResolved === true
        || (item?.ownerResolved === undefined && item?.attachedToUser === true)
    },
    stateColor(value) {
      return {
        ACTIVE: 'success',
        PAYMENT_RETRY_WITH_ACCESS: 'info',
        ON_HOLD_NO_ACCESS: 'warning',
        EXPIRED: 'error',
      }[value] || 'secondary'
    },
    stateTextColor(value) {
      return value === 'PAYMENT_RETRY_WITH_ACCESS' ? 'white' : undefined
    },
    platformLabel(value) {
      return {
        IOS: 'iOS',
        ANDROID: 'Android',
        MANUAL: 'Manuel',
        UNKNOWN: 'Bilinmiyor',
      }[value] || value || '-'
    },
    resolvedPlatform(item) {
      if (item?.customPremium) {
        return 'MANUAL'
      }
      if (item?.premiumPlatform && item.premiumPlatform !== 'UNKNOWN') {
        return item.premiumPlatform
      }

      return item?.premiumDeviceType || 'UNKNOWN'
    },
    packageMeta(item) {
      const parts = []
      if (item.packageId) {
        parts.push(`#${item.packageId}`)
      }
      if (item.packageDay) {
        parts.push(`${item.packageDay} gün`)
      }
      if (item.packagePrice !== null && item.packagePrice !== undefined) {
        parts.push(this.formatCurrency(item.packagePrice))
      }

      return parts.join(' · ') || 'Paket detayı yok'
    },
    premiumPlanPhaseLabel(item) {
      const plan = item?.packageTime || item?.contractPackageLabel || '-'
      if (item?.trialActive) {
        return `${plan} · 3 günlük ücretsiz deneme aktif`
      }
      if (item?.trialConversionPending) {
        return `${plan} · ücretli dönüşüm bekleniyor`
      }

      return plan
    },
    premiumListPlanLabel(item) {
      const plan = item?.packageTime || item?.contractPackageLabel || 'Atama yok'
      const trial = item?.trialActive
        || item?.trialConversionPending
        || item?.storeEvidenceFreeTrial === true

      return trial ? `${plan} deneme` : plan
    },
    yesNoLabel(value) {
      return value ? 'Evet' : 'Hayır'
    },
    evidenceBooleanLabel(value) {
      if (value === null || value === undefined) {
        return 'Doğrulanmadı'
      }

      return value ? 'Ücretsiz deneme' : 'Ücretli faz'
    },
    evidenceOfferLabel(item) {
      return [item?.storeEvidenceOfferType, item?.storeEvidenceOfferDiscountType]
        .filter(Boolean)
        .join(' · ') || 'Teklif sinyali yok'
    },
    formatCurrency(value, currency = 'TRY') {
      if (value === null || value === undefined || value === '') {
        return '-'
      }

      try {
        return new Intl.NumberFormat('tr-TR', {
          style: 'currency',
          currency: currency || 'TRY',
          maximumFractionDigits: 2,
        }).format(Number(value) || 0)
      } catch (error) {
        return `${Number(value) || 0} ${currency || ''}`.trim()
      }
    },
    formatTimestamp(value) {
      if (!value) {
        return '-'
      }

      return new Intl.DateTimeFormat('tr-TR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value))
    },
  },
}
</script>

<style lang="scss" scoped>
.premium-summary-card {
  min-height: 132px;
}

.premium-toolbar {
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.premium-toolbar__meta,
.premium-toolbar__controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.premium-search {
  min-width: 320px;
  max-width: 420px;
}

.premium-status-filter {
  min-width: 230px;
  max-width: 280px;
}

.premium-user-cell {
  min-width: 220px;
}

.premium-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.premium-cleanup-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.premium-cleanup-metric {
  display: grid;
  gap: 6px;
  padding: 14px 16px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  background: rgba(248, 250, 252, 0.8);
}

.premium-cleanup-section {
  margin-top: 20px;
}

@media (max-width: 960px) {
  .premium-search,
  .premium-status-filter {
    min-width: 100%;
    max-width: none;
  }

  .premium-cleanup-grid {
    grid-template-columns: 1fr;
  }
}
</style>
